import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid2 as Grid,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress,
  Alert, TablePagination, TextField, InputAdornment,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  AccountBalanceWallet, CreditCard, Store, SwapHoriz,
  Refresh, Search as SearchIcon, Watch,
  Add as AddIcon, Delete as DeleteIcon, Block as BlockIcon,
  CheckCircle as UnblockIcon, Inventory as StockIcon,
  AccountBalance as BankIcon, PlayArrow as RunIcon,
  Edit as EditIcon, VerifiedUser as VerifiedIcon,
  Nfc as NfcIcon, PersonAdd as PersonAddIcon,
  TrendingUp as UpgradeIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Overview {
  circulacao:  { total: number };
  topups:      { hoje: number; semana: number; mes: number; num_hoje: number };
  pagamentos:  { hoje: number; num_hoje: number; semana: number };
  liquidacao:  { total_pendente: number; num_comerciantes: number };
  pulseiras:   { total: number; activas: number; bloqueadas: number };
}

interface Transacao {
  id: number; tipo: string; valor: number;
  descricao: string | null; status: string; created_at: string;
  comerciante_entity_id: number | null; nfc_uid: string | null;
  saldo_antes: number; saldo_depois: number;
}

interface Comerciante {
  entity_id: number; saldo_pendente: number; total_recebido: number;
  ultima_liquidacao: string | null; nome: string | null;
  iban_bcn: string | null; nome_bcn: string | null; iban_bcn_verified: number;
}

interface Liquidacao {
  id: number; entity_id: number; valor: number; num_transacoes: number;
  periodo_inicio: string; periodo_fim: string;
  referencia_transferencia: string | null; status: string;
  processado_em: string | null; created_at: string;
  comerciante_nome: string | null;
}

interface SettlementResult {
  processed: number; failed: number; total_cve: number;
  started_at: string; finished_at: string;
}

interface Pulseira {
  id: number; nfc_uid: string; user_id: string; wallet_id: string;
  label: string | null; status: string; activated_at: string | null;
}

interface WristbandAdmin {
  id: number; nfc_uid: string; label: string | null; status: string;
  kyc_level: string; saldo: number;
  limit_no_pin_cve: number; limit_daily_cve: number; limit_monthly_cve: number;
  issued_at: string | null; activated_at: string | null; event_id: number | null;
  user_email: string | null; user_nome: string | null; user_telefone: string | null;
  issued_by_email: string | null; issued_by_nome: string | null;
}

interface StockItem {
  id: number;
  nfc_uid: string;
  nfc_serial: string | null;
  status: 'AVAILABLE' | 'ASSIGNED' | 'ACTIVE' | 'BLOCKED' | 'LOST' | 'RETURNED';
  evento_id: number | null;
  participante_nome: string | null;
  saldo: number;
  saldo_bonus: number;
  ativada_at: string | null;
  bloqueada_at: string | null;
  bloqueio_motivo: string | null;
  created_at: string;
}

interface StockStats {
  AVAILABLE: number; ASSIGNED: number; ACTIVE: number;
  BLOCKED: number; LOST: number; RETURNED: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v?: number | null) =>
  v == null ? '—' : `${Math.round(v).toLocaleString('pt-PT')}$`;

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const TIPO_COLOR: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  TOPUP: 'success', PAYMENT: 'error', BONUS: 'info', REFUND: 'warning', CASHOUT: 'default',
};

const STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  COMPLETED: 'success', FAILED: 'error', PENDING: 'warning',
};

const STOCK_STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  AVAILABLE: 'success', ASSIGNED: 'info', ACTIVE: 'success',
  BLOCKED: 'error', LOST: 'error', RETURNED: 'default',
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ title, value, sub, icon, color = '#6B2FA0' }: {
  title: string; value: string; sub?: string;
  icon: React.ReactNode; color?: string;
}) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}18`, color, display: 'flex' }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * Separadores desta página, por nome.
 *
 * Existem para o menu poder apontar directamente a um deles. A entrada «Pulseiras
 * NFC» levava a um `<ComingSoon />` — uma página por construir para gerir aquilo
 * que ESTA página já geria, só que em silêncio: o separador de onboarding chamava
 * um endpoint que devolvia 500 e o `catch` era mudo, portanto aparecia vazio.
 *
 * Um segundo ecrã para a mesma coisa seria repetir a confusão que se acabou de
 * arrumar no menu.
 */
export const ABAS = {
  visaoGeral: 0,
  transaccoes: 1,
  comerciantes: 2,
  pulseiras: 3,
  stock: 4,
  liquidacoes: 5,
  onboarding: 6,
} as const;

const CashlessAdmin: React.FC<{ abaInicial?: number }> = ({ abaInicial = 0 }) => {
  const [tab, setTab] = useState(abaInicial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);

  // Transacções
  const [transacoes, setTransacoes]   = useState<Transacao[]>([]);
  const [txTotal, setTxTotal]         = useState(0);
  const [txPage, setTxPage]           = useState(0);
  const [txLoading, setTxLoading]     = useState(false);
  const [txSearch, setTxSearch]       = useState('');

  // Comerciantes
  const [comerciantes, setComercaintes] = useState<Comerciante[]>([]);
  const [cmLoading, setCmLoading]     = useState(false);

  // Pulseiras (activadas)
  const [pulseiras, setPulseiras]     = useState<Pulseira[]>([]);
  const [pbTotal, setPbTotal]         = useState(0);
  const [pbPage, setPbPage]           = useState(0);
  const [pbLoading, setPbLoading]     = useState(false);
  const [pbSearch, setPbSearch]       = useState('');

  // Liquidações BCN
  const [liquidacoes, setLiquidacoes]     = useState<Liquidacao[]>([]);
  const [liqTotal, setLiqTotal]           = useState(0);
  const [liqPage, setLiqPage]             = useState(0);
  const [liqLoading, setLiqLoading]       = useState(false);
  const [settlementRunning, setSettlementRunning] = useState(false);
  const [settlementResult, setSettlementResult]   = useState<SettlementResult | null>(null);
  const [settlementError, setSettlementError]     = useState<string | null>(null);

  // IBAN dialog (Comerciantes)
  const [ibanDialogOpen, setIbanDialogOpen]     = useState(false);
  const [ibanMerchant, setIbanMerchant]         = useState<Comerciante | null>(null);
  const [ibanValue, setIbanValue]               = useState('');
  const [ibanNome, setIbanNome]                 = useState('');
  const [ibanVerified, setIbanVerified]         = useState(false);
  const [ibanSaving, setIbanSaving]             = useState(false);

  // Stock
  const [stockItems, setStockItems]   = useState<StockItem[]>([]);
  const [stockStats, setStockStats]   = useState<StockStats | null>(null);
  const [stLoading, setStLoading]     = useState(false);
  const [stSearch, setStSearch]       = useState('');
  const [stStatusFilter, setStStatusFilter] = useState('');
  const [addDialogOpen, setAddDialogOpen]   = useState(false);
  const [addUids, setAddUids]         = useState('');
  const [addLoading, setAddLoading]   = useState(false);
  const [addResult, setAddResult]     = useState<string | null>(null);

  // Onboarding (Bloco E)
  const [onbWristbands, setOnbWristbands]     = useState<WristbandAdmin[]>([]);
  const [onbTotal, setOnbTotal]               = useState(0);
  const [onbLoading, setOnbLoading]           = useState(false);
  const [onbKycFilter, setOnbKycFilter]       = useState('');
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueForm, setIssueForm]             = useState({
    nfc_uid: '', user_identifier: '', label: '', kyc_level: 'BASIC', event_id: '',
  });
  const [issueLoading, setIssueLoading]       = useState(false);
  const [issueResult, setIssueResult]         = useState<string | null>(null);
  const [kycDialogOpen, setKycDialogOpen]     = useState(false);
  const [kycTarget, setKycTarget]             = useState<WristbandAdmin | null>(null);
  const [kycNewLevel, setKycNewLevel]         = useState('BASIC');
  const [kycSaving, setKycSaving]             = useState(false);

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadOverview = useCallback(async () => {
    try {
      const res = await api.get('/admin/cashless/overview');
      if (res.data.success) setOverview(res.data.data);
    } catch { /* silencioso */ }
  }, []);

  const loadTransacoes = useCallback(async (page = 0) => {
    setTxLoading(true);
    try {
      const res = await api.get('/admin/cashless/transactions', {
        params: { page: page + 1, limit: 20, ...(txSearch ? { search: txSearch } : {}) },
      });
      if (res.data.success) {
        setTransacoes(res.data.data || []);
        setTxTotal(res.data.pagination?.total || 0);
      }
    } finally { setTxLoading(false); }
  }, [txSearch]);

  const loadComercantes = useCallback(async () => {
    setCmLoading(true);
    try {
      const res = await api.get('/admin/cashless/comerciantes');
      if (res.data.success) setComercaintes(res.data.data || []);
    } finally { setCmLoading(false); }
  }, []);

  const loadPulseiras = useCallback(async (page = 0) => {
    setPbLoading(true);
    try {
      const res = await api.get('/admin/cashless/wristbands', {
        params: { page: page + 1, limit: 20, ...(pbSearch ? { search: pbSearch } : {}) },
      }).catch(() => null);
      if (res?.data?.success) {
        setPulseiras(res.data.data || []);
        setPbTotal(res.data.pagination?.total || 0);
      }
    } finally { setPbLoading(false); }
  }, [pbSearch]);

  const loadLiquidacoes = useCallback(async (page = 0) => {
    setLiqLoading(true);
    try {
      const res = await api.get('/admin/cashless/liquidacoes', {
        params: { page: page + 1, limit: 20 },
      });
      if (res.data.success) {
        setLiquidacoes(res.data.data || []);
        setLiqTotal(res.data.pagination?.total || 0);
      }
    } catch { /* silencioso */ }
    finally { setLiqLoading(false); }
  }, []);

  const runSettlement = async () => {
    setSettlementRunning(true);
    setSettlementResult(null);
    setSettlementError(null);
    try {
      const res = await api.post('/admin/cashless/settlement/run');
      if (res.data.success) {
        setSettlementResult(res.data.data);
        loadLiquidacoes(0);
        loadOverview();
      }
    } catch (err: any) {
      setSettlementError(err?.response?.data?.error || err.message);
    } finally { setSettlementRunning(false); }
  };

  const openIbanDialog = (c: Comerciante) => {
    setIbanMerchant(c);
    setIbanValue(c.iban_bcn || '');
    setIbanNome(c.nome_bcn || c.nome || '');
    setIbanVerified(!!c.iban_bcn_verified);
    setIbanDialogOpen(true);
  };

  const saveIban = async () => {
    if (!ibanMerchant) return;
    setIbanSaving(true);
    try {
      await api.put(`/admin/cashless/comerciantes/${ibanMerchant.entity_id}/iban`, {
        iban_bcn: ibanValue, nome_bcn: ibanNome, verified: ibanVerified,
      });
      setIbanDialogOpen(false);
      loadComercantes();
    } catch (err: any) {
      alert('Erro: ' + (err?.response?.data?.error || err.message));
    } finally { setIbanSaving(false); }
  };

  const loadStock = useCallback(async () => {
    setStLoading(true);
    try {
      const res = await api.get('/portal/wristband-stock', {
        params: {
          limit: 500,
          ...(stSearch ? { search: stSearch } : {}),
          ...(stStatusFilter ? { status: stStatusFilter } : {}),
        },
      });
      if (res.data.success) {
        setStockItems(res.data.data || []);
        setStockStats(res.data.stats || null);
      }
    } catch { /* silencioso */ }
    finally { setStLoading(false); }
  }, [stSearch, stStatusFilter]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try { await loadOverview(); }
      catch (e: any) { setError(e.message); }
      finally { setLoading(false); }
    };
    init();
  }, [loadOverview]);

  const loadOnboarding = useCallback(async () => {
    setOnbLoading(true);
    try {
      const res = await api.get('/admin/nfc/admin/wristbands', {
        params: { limit: 50, ...(onbKycFilter ? { kyc_level: onbKycFilter } : {}) },
      });
      setOnbWristbands(res.data.wristbands || []);
      setOnbTotal(res.data.total || 0);
    } catch { /* silencioso */ }
    finally { setOnbLoading(false); }
  }, [onbKycFilter]);

  useEffect(() => { if (tab === 1) loadTransacoes(txPage); }, [tab, txPage, loadTransacoes]);
  useEffect(() => { if (tab === 2) loadComercantes(); }, [tab, loadComercantes]);
  useEffect(() => { if (tab === 3) loadPulseiras(pbPage); }, [tab, pbPage, loadPulseiras]);
  useEffect(() => { if (tab === 4) loadStock(); }, [tab, loadStock]);
  useEffect(() => { if (tab === 5) loadLiquidacoes(liqPage); }, [tab, liqPage, loadLiquidacoes]);
  useEffect(() => { if (tab === 6) loadOnboarding(); }, [tab, loadOnboarding]);

  // ── Onboarding actions ────────────────────────────────────────────────────

  const handleIssueWristband = async () => {
    if (!issueForm.nfc_uid.trim() || !issueForm.user_identifier.trim()) return;
    setIssueLoading(true);
    setIssueResult(null);
    try {
      const payload = {
        nfc_uid: issueForm.nfc_uid.trim(),
        user_identifier: issueForm.user_identifier.trim(),
        kyc_level: issueForm.kyc_level,
        ...(issueForm.label.trim()    ? { label: issueForm.label.trim() }         : {}),
        ...(issueForm.event_id.trim() ? { event_id: Number(issueForm.event_id) }  : {}),
      };
      const res = await api.post('/admin/nfc/wristbands/issue', payload);
      setIssueResult(`✅ Pulseira emitida — ID ${res.data.id} · KYC: ${res.data.kyc_level}`);
      setIssueForm({ nfc_uid: '', user_identifier: '', label: '', kyc_level: 'BASIC', event_id: '' });
      loadOnboarding();
    } catch (err: any) {
      setIssueResult(`❌ ${err?.response?.data?.error || err.message}`);
    } finally {
      setIssueLoading(false);
    }
  };

  const handleKycUpgrade = async () => {
    if (!kycTarget) return;
    setKycSaving(true);
    try {
      await api.put(`/admin/nfc/admin/wristbands/${kycTarget.nfc_uid}/kyc`, { kyc_level: kycNewLevel });
      setKycDialogOpen(false);
      loadOnboarding();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao actualizar KYC');
    } finally {
      setKycSaving(false);
    }
  };

  // ── Stock actions ──────────────────────────────────────────────────────────

  const handleAddBracelets = async () => {
    const lines = addUids.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setAddLoading(true);
    setAddResult(null);
    try {
      const items = lines.map(uid => ({ nfcUid: uid }));
      const res = await api.post('/portal/wristband-stock',
        lines.length === 1 ? { nfcUid: lines[0] } : { items }
      );
      setAddResult(`✅ ${res.data.created} adicionados${res.data.failed ? ` · ⚠️ ${res.data.failed} falharam` : ''}`);
      setAddUids('');
      loadStock();
    } catch (err: any) {
      setAddResult(`❌ Erro: ${err?.response?.data?.error || err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const handleBlock = async (item: StockItem) => {
    const isBlocked = item.status === 'BLOCKED';
    const motivo = isBlocked ? undefined : window.prompt('Motivo do bloqueio (opcional):') ?? undefined;
    try {
      await api.patch(`/portal/wristband-stock/${item.id}`, {
        status: isBlocked ? 'AVAILABLE' : 'BLOCKED',
        ...(motivo ? { motivo } : {}),
      });
      loadStock();
    } catch (err: any) {
      alert('Erro: ' + (err?.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (item: StockItem) => {
    if (!window.confirm(`Eliminar bracelet ${item.nfc_uid}?`)) return;
    try {
      await api.delete(`/portal/wristband-stock/${item.id}`);
      loadStock();
    } catch (err: any) {
      alert('Erro: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  const ov = overview;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Cashless Pagali Pay</Typography>
        <Box sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
          onClick={() => { loadOverview(); }}>
          <Refresh fontSize="small" />
          <Typography variant="body2">Actualizar</Typography>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Em circulação" value={fmt(ov?.circulacao?.total)}
            sub="Saldo total nas wallets com pulseira activa"
            icon={<AccountBalanceWallet />} color="#6B2FA0" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Carregamentos hoje" value={fmt(ov?.topups?.hoje)}
            sub={`${ov?.topups?.num_hoje || 0} operações`}
            icon={<CreditCard />} color="#00897B" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Pagamentos NFC hoje" value={fmt(ov?.pagamentos?.hoje)}
            sub={`${ov?.pagamentos?.num_hoje || 0} transacções`}
            icon={<SwapHoriz />} color="#1565C0" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Liquidação pendente" value={fmt(ov?.liquidacao?.total_pendente)}
            sub={`${ov?.liquidacao?.num_comerciantes || 0} comerciantes`}
            icon={<Store />} color="#E65100" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Pulseiras activas" value={String(ov?.pulseiras?.activas || 0)}
            sub={`${ov?.pulseiras?.bloqueadas || 0} bloqueadas · ${ov?.pulseiras?.total || 0} total`}
            icon={<Watch />} color="#AD1457" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Carregamentos semana" value={fmt(ov?.topups?.semana)}
            icon={<CreditCard />} color="#00897B" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Carregamentos mês" value={fmt(ov?.topups?.mes)}
            icon={<CreditCard />} color="#00897B" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Pagamentos semana" value={fmt(ov?.pagamentos?.semana)}
            icon={<SwapHoriz />} color="#1565C0" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Visão Geral" />
          <Tab label="Transacções" />
          <Tab label="Comerciantes" />
          <Tab label="Pulseiras" />
          <Tab label="Stock NFC" icon={<StockIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Liquidações BCN" icon={<BankIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Onboarding NFC" icon={<NfcIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {/* Tab 0: Visão Geral */}
        {tab === 0 && (
          <Box p={3}>
            <Typography variant="body2" color="text.secondary">
              Os indicadores acima mostram o estado actual do sistema cashless.
              Use as tabs para ver transacções, comerciantes, pulseiras e stock de bracelets.
            </Typography>
          </Box>
        )}

        {/* Tab 1: Transacções */}
        {tab === 1 && (
          <Box>
            <Box p={2} display="flex" gap={2}>
              <TextField size="small" placeholder="Pesquisar por utilizador, NFC UID..."
                value={txSearch} onChange={e => setTxSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadTransacoes(0)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 320 }} />
            </Box>
            {txLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell><TableCell>Tipo</TableCell>
                      <TableCell align="right">Valor</TableCell><TableCell>Descrição</TableCell>
                      <TableCell>NFC UID</TableCell><TableCell>Status</TableCell><TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transacoes.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" py={3}>Sem transacções</Typography></TableCell></TableRow>
                    ) : transacoes.map(t => (
                      <TableRow key={t.id} hover>
                        <TableCell><Typography variant="caption" fontFamily="monospace">#{t.id}</Typography></TableCell>
                        <TableCell><Chip size="small" label={t.tipo} color={TIPO_COLOR[t.tipo] || 'default'} /></TableCell>
                        <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(t.valor)}</Typography></TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{t.descricao || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontFamily="monospace">{t.nfc_uid || '—'}</Typography></TableCell>
                        <TableCell><Chip size="small" label={t.status} color={STATUS_COLOR[t.status] || 'default'} /></TableCell>
                        <TableCell><Typography variant="caption">{fmtDate(t.created_at)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination component="div" count={txTotal} page={txPage} rowsPerPage={20}
              onPageChange={(_, p) => setTxPage(p)} rowsPerPageOptions={[20]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} />
          </Box>
        )}

        {/* Tab 2: Comerciantes */}
        {tab === 2 && (
          <Box>
            {cmLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Comerciante</TableCell>
                      <TableCell align="right">Saldo Pendente</TableCell>
                      <TableCell align="right">Total Recebido</TableCell>
                      <TableCell>IBAN BCN</TableCell>
                      <TableCell>Última Liquidação</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comerciantes.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={3}>Sem comerciantes</Typography></TableCell></TableRow>
                    ) : comerciantes.map(c => (
                      <TableRow key={c.entity_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{c.nome || '—'}</Typography>
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">{c.entity_id}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color={c.saldo_pendente > 0 ? 'warning.main' : 'text.secondary'}>
                            {fmt(c.saldo_pendente)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmt(c.total_recebido)}</TableCell>
                        <TableCell>
                          {c.iban_bcn ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" fontFamily="monospace">{c.iban_bcn}</Typography>
                              {c.iban_bcn_verified ? (
                                <Tooltip title="IBAN verificado — liquidações automáticas activas">
                                  <VerifiedIcon fontSize="small" color="success" />
                                </Tooltip>
                              ) : (
                                <Chip size="small" label="Por verificar" color="warning" />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Não configurado</Typography>
                          )}
                        </TableCell>
                        <TableCell><Typography variant="caption">{fmtDate(c.ultima_liquidacao)}</Typography></TableCell>
                        <TableCell>
                          <Tooltip title="Configurar IBAN BCN">
                            <IconButton size="small" onClick={() => openIbanDialog(c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 3: Pulseiras activadas */}
        {tab === 3 && (
          <Box>
            <Box p={2}>
              <TextField size="small" placeholder="Pesquisar por NFC UID ou user_id..."
                value={pbSearch} onChange={e => setPbSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadPulseiras(0)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 320 }} />
            </Box>
            {pbLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NFC UID</TableCell><TableCell>Utilizador</TableCell>
                      <TableCell>Label</TableCell><TableCell>Status</TableCell><TableCell>Activada em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pulseiras.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary" py={3}>Sem pulseiras{pbSearch ? ' para esta pesquisa' : ''}</Typography></TableCell></TableRow>
                    ) : pulseiras.map(p => (
                      <TableRow key={p.id} hover>
                        <TableCell><Typography variant="caption" fontFamily="monospace" fontWeight={600}>{p.nfc_uid}</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontFamily="monospace">{String(p.user_id).substring(0, 12)}...</Typography></TableCell>
                        <TableCell>{p.label || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={p.status}
                            color={p.status === 'ACTIVE' ? 'success' : p.status === 'BLOCKED' ? 'error' : 'default'} />
                        </TableCell>
                        <TableCell><Typography variant="caption">{fmtDate(p.activated_at)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination component="div" count={pbTotal} page={pbPage} rowsPerPage={20}
              onPageChange={(_, p) => setPbPage(p)} rowsPerPageOptions={[20]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} />
          </Box>
        )}

        {/* Tab 4: Stock NFC */}
        {tab === 4 && (
          <Box>
            {/* Stats do stock */}
            {stockStats && (
              <Box display="flex" gap={1.5} p={2} flexWrap="wrap">
                {Object.entries(stockStats).map(([status, count]) => (
                  <Chip key={status} label={`${status}: ${count}`}
                    color={STOCK_STATUS_COLOR[status] || 'default'} size="small" variant="outlined" />
                ))}
              </Box>
            )}

            {/* Filtros + botão adicionar */}
            <Box p={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField size="small" placeholder="Pesquisar UID, serial ou nome..."
                value={stSearch} onChange={e => setStSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadStock()}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 280 }} />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Estado</InputLabel>
                <Select value={stStatusFilter} label="Estado"
                  onChange={e => setStStatusFilter(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {['AVAILABLE', 'ASSIGNED', 'ACTIVE', 'BLOCKED', 'LOST', 'RETURNED'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box flex={1} />
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => { setAddDialogOpen(true); setAddResult(null); }}
                sx={{ bgcolor: '#6B2FA0', '&:hover': { bgcolor: '#4e2880' } }}>
                Adicionar Bracelets
              </Button>
            </Box>

            {stLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><b>NFC UID</b></TableCell>
                      <TableCell><b>Serial</b></TableCell>
                      <TableCell align="center"><b>Estado</b></TableCell>
                      <TableCell><b>Participante / Funcionário</b></TableCell>
                      <TableCell align="right"><b>Saldo</b></TableCell>
                      <TableCell><b>Activada em</b></TableCell>
                      <TableCell><b>Registada em</b></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary" py={3}>
                            Nenhum bracelet no stock
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : stockItems.map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace" fontWeight={700}>
                            {item.nfc_uid}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {item.nfc_serial || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={item.status}
                            color={STOCK_STATUS_COLOR[item.status] || 'default'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {item.participante_nome || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {item.saldo > 0 || item.saldo_bonus > 0
                            ? `${fmt(item.saldo + item.saldo_bonus)}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtDate(item.ativada_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtDate(item.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title={item.status === 'BLOCKED' ? 'Desbloquear' : 'Bloquear'}>
                              <IconButton size="small" onClick={() => handleBlock(item)}>
                                {item.status === 'BLOCKED'
                                  ? <UnblockIcon fontSize="small" color="success" />
                                  : <BlockIcon fontSize="small" color="warning" />}
                              </IconButton>
                            </Tooltip>
                            {item.status === 'AVAILABLE' && (
                              <Tooltip title="Eliminar">
                                <IconButton size="small" onClick={() => handleDelete(item)}>
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 5: Liquidações BCN */}
        {tab === 5 && (
          <Box p={3}>
            {/* Cabeçalho com botão de run */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Typography variant="h6" fontWeight={600}>Liquidações BCN D+1</Typography>
                <Typography variant="body2" color="text.secondary">
                  Transferências automáticas para contas operacionais dos comerciantes.
                  Execução diária às 23:59 CVT — pode forçar manualmente abaixo.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={settlementRunning ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                onClick={runSettlement}
                disabled={settlementRunning}
                sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' }, minWidth: 200 }}
              >
                {settlementRunning ? 'A processar...' : 'Executar liquidação agora'}
              </Button>
            </Box>

            {/* Resultado da última execução manual */}
            {settlementError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSettlementError(null)}>
                {settlementError}
              </Alert>
            )}
            {settlementResult && (
              <Alert severity={settlementResult.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}
                onClose={() => setSettlementResult(null)}>
                Liquidação concluída — {settlementResult.processed} comerciantes processados
                {settlementResult.failed > 0 ? ` · ${settlementResult.failed} falharam` : ''}
                {' · '}Total: <strong>{Math.round(settlementResult.total_cve).toLocaleString('pt-PT')}$</strong>
              </Alert>
            )}

            {/* Tabela de liquidações */}
            {liqLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><b>ID</b></TableCell>
                      <TableCell><b>Comerciante</b></TableCell>
                      <TableCell align="right"><b>Valor</b></TableCell>
                      <TableCell align="right"><b>Transacções</b></TableCell>
                      <TableCell><b>Período</b></TableCell>
                      <TableCell><b>Ref. BCN</b></TableCell>
                      <TableCell><b>Estado</b></TableCell>
                      <TableCell><b>Processado em</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liquidacoes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary" py={3}>Sem liquidações registadas</Typography>
                        </TableCell>
                      </TableRow>
                    ) : liquidacoes.map(l => (
                      <TableRow key={l.id} hover>
                        <TableCell><Typography variant="caption" fontFamily="monospace">#{l.id}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2">{l.comerciante_nome || '—'}</Typography>
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">{l.entity_id}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>{fmt(l.valor)}</Typography>
                        </TableCell>
                        <TableCell align="right">{l.num_transacoes}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {fmtDate(l.periodo_inicio)} → {fmtDate(l.periodo_fim)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace" noWrap sx={{ maxWidth: 140 }}>
                            {l.referencia_transferencia || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={l.status}
                            color={l.status === 'PROCESSED' ? 'success' : l.status === 'FAILED' ? 'error' : 'warning'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtDate(l.processado_em)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination component="div" count={liqTotal} page={liqPage} rowsPerPage={20}
              onPageChange={(_, p) => setLiqPage(p)} rowsPerPageOptions={[20]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} />
          </Box>
        )}
      </Card>

      {/* Tab 6: Onboarding NFC */}
      {tab === 6 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>Onboarding & Emissão de Pulseiras NFC</Typography>
              <Box display="flex" gap={1}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Filtrar KYC</InputLabel>
                  <Select value={onbKycFilter} label="Filtrar KYC"
                    onChange={e => setOnbKycFilter(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="BASIC">BASIC</MenuItem>
                    <MenuItem value="STANDARD">STANDARD</MenuItem>
                    <MenuItem value="ENHANCED">ENHANCED</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={loadOnboarding}
                  startIcon={<Refresh fontSize="small" />}>Actualizar</Button>
                <Button variant="contained" size="small"
                  onClick={() => { setIssueResult(null); setIssueDialogOpen(true); }}
                  startIcon={<PersonAddIcon fontSize="small" />}
                  sx={{ bgcolor: '#6B2FA0', '&:hover': { bgcolor: '#4e2880' } }}>
                  Emitir Pulseira
                </Button>
              </Box>
            </Box>

            {onbLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : onbWristbands.length === 0 ? (
              <Box textAlign="center" py={4}>
                <NfcIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">Nenhuma pulseira emitida ainda.</Typography>
                <Typography variant="caption" color="text.secondary">
                  Usa o botão "Emitir Pulseira" para registar o primeiro NFC UID.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NFC UID</TableCell>
                      <TableCell>Titular</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell>KYC</TableCell>
                      <TableCell>Limites (s/PIN / dia / mês)</TableCell>
                      <TableCell>Saldo</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Emissão</TableCell>
                      <TableCell>Emitido por</TableCell>
                      <TableCell align="center">Acções</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {onbWristbands.map(wb => (
                      <TableRow key={wb.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {wb.nfc_uid}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{wb.user_nome || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{wb.user_email || wb.user_telefone || '—'}</Typography>
                        </TableCell>
                        <TableCell>{wb.label || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                        <TableCell>
                          <Chip size="small" label={wb.kyc_level}
                            color={wb.kyc_level === 'ENHANCED' ? 'success' : wb.kyc_level === 'STANDARD' ? 'info' : 'default'} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          {wb.limit_no_pin_cve?.toLocaleString()}$ / {wb.limit_daily_cve?.toLocaleString()}$ / {wb.limit_monthly_cve?.toLocaleString()}$
                        </TableCell>
                        <TableCell>{fmt(wb.saldo)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={wb.status}
                            color={wb.status === 'ACTIVE' ? 'success' : wb.status === 'BLOCKED' ? 'error' : 'default'} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{fmtDate(wb.issued_at)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">{wb.issued_by_nome || wb.issued_by_email || '—'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Actualizar nível KYC">
                            <IconButton size="small"
                              onClick={() => { setKycTarget(wb); setKycNewLevel(wb.kyc_level); setKycDialogOpen(true); }}>
                              <UpgradeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              Total: {onbTotal} pulseiras
            </Typography>
          </Box>
        </Card>
      )}

      {/* Dialog: Configurar IBAN BCN */}
      <Dialog open={ibanDialogOpen} onClose={() => setIbanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar IBAN BCN — {ibanMerchant?.nome || ibanMerchant?.entity_id}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            O IBAN é usado nas liquidações automáticas D+1. Marcar como "verificado" activa as transferências BCN para este comerciante.
          </Typography>
          <TextField
            label="IBAN BCN" fullWidth size="small" sx={{ mb: 2 }}
            value={ibanValue} onChange={e => setIbanValue(e.target.value.toUpperCase())}
            placeholder="CV05000100000000000000000"
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <TextField
            label="Nome titular da conta (conforme BCN)" fullWidth size="small" sx={{ mb: 2 }}
            value={ibanNome} onChange={e => setIbanNome(e.target.value)}
          />
          <Box display="flex" alignItems="center" gap={1}>
            <input type="checkbox" id="iban-verified" checked={ibanVerified}
              onChange={e => setIbanVerified(e.target.checked)} />
            <label htmlFor="iban-verified">
              <Typography variant="body2">IBAN verificado — activar liquidações automáticas</Typography>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIbanDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveIban}
            disabled={ibanSaving || !ibanValue.trim() || !ibanNome.trim()}
            startIcon={ibanSaving ? <CircularProgress size={16} /> : <BankIcon />}
            sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' } }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Emitir Pulseira NFC */}
      <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NfcIcon /> Emitir Nova Pulseira NFC
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Regista um chip NTAG 424 DNA lido pelo leitor NFC associando-o ao utilizador Pagali.
          </Typography>
          <TextField label="NFC UID *" fullWidth size="small" sx={{ mb: 2 }}
            value={issueForm.nfc_uid}
            onChange={e => setIssueForm(f => ({ ...f, nfc_uid: e.target.value.trim().toUpperCase() }))}
            placeholder="04:AB:12:CD:EF:01:80"
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <TextField label="Email, telefone ou UID Firebase do titular *" fullWidth size="small" sx={{ mb: 2 }}
            value={issueForm.user_identifier}
            onChange={e => setIssueForm(f => ({ ...f, user_identifier: e.target.value.trim() }))}
            placeholder="titular@email.cv"
          />
          <TextField label="Label da pulseira" fullWidth size="small" sx={{ mb: 2 }}
            value={issueForm.label}
            onChange={e => setIssueForm(f => ({ ...f, label: e.target.value }))}
            placeholder="Ex: Mindelo Mágico 2026"
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Nível KYC</InputLabel>
            <Select value={issueForm.kyc_level} label="Nível KYC"
              onChange={e => setIssueForm(f => ({ ...f, kyc_level: e.target.value }))}>
              <MenuItem value="BASIC">BASIC — 1.000$ sem PIN · 10.000$/dia · 50.000$/mês</MenuItem>
              <MenuItem value="STANDARD">STANDARD — 2.000$ sem PIN · 25.000$/dia · 100.000$/mês</MenuItem>
              <MenuItem value="ENHANCED">ENHANCED — 5.000$ sem PIN · 50.000$/dia · 200.000$/mês</MenuItem>
            </Select>
          </FormControl>
          <TextField label="ID do Evento (opcional)" fullWidth size="small" sx={{ mb: 2 }}
            value={issueForm.event_id}
            onChange={e => setIssueForm(f => ({ ...f, event_id: e.target.value }))}
            placeholder="Deixa vazio para uso geral"
          />
          {issueResult && (
            <Alert severity={issueResult.startsWith('✅') ? 'success' : 'error'} sx={{ mt: 1 }}>
              {issueResult}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialogOpen(false)}>Fechar</Button>
          <Button variant="contained" onClick={handleIssueWristband}
            disabled={issueLoading || !issueForm.nfc_uid.trim() || !issueForm.user_identifier.trim()}
            startIcon={issueLoading ? <CircularProgress size={16} /> : <PersonAddIcon />}
            sx={{ bgcolor: '#6B2FA0', '&:hover': { bgcolor: '#4e2880' } }}>
            Emitir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Actualizar KYC */}
      <Dialog open={kycDialogOpen} onClose={() => setKycDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Actualizar Nível KYC</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            UID: <strong style={{ fontFamily: 'monospace' }}>{kycTarget?.nfc_uid}</strong>
            {kycTarget?.user_nome && <> · {kycTarget.user_nome}</>}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Novo nível KYC</InputLabel>
            <Select value={kycNewLevel} label="Novo nível KYC"
              onChange={e => setKycNewLevel(e.target.value)}>
              <MenuItem value="BASIC">BASIC — 1.000$ sem PIN · 10.000$/dia</MenuItem>
              <MenuItem value="STANDARD">STANDARD — 2.000$ sem PIN · 25.000$/dia</MenuItem>
              <MenuItem value="ENHANCED">ENHANCED — 5.000$ sem PIN · 50.000$/dia</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKycDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleKycUpgrade}
            disabled={kycSaving || kycNewLevel === kycTarget?.kyc_level}
            startIcon={kycSaving ? <CircularProgress size={16} /> : <UpgradeIcon />}
            sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' } }}>
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Adicionar Bracelets */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Bracelets ao Stock</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Introduz um UID por linha. Formato hex aceite: <code>A1B2C3D4</code> ou <code>A1:B2:C3:D4</code>
          </Typography>
          <TextField
            label="NFC UIDs (um por linha)"
            multiline rows={6}
            value={addUids}
            onChange={e => setAddUids(e.target.value)}
            fullWidth
            placeholder={'A1B2C3D4\nE5F6G7H8\nA1:B2:C3:D4'}
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          {addResult && (
            <Alert severity={addResult.startsWith('✅') ? 'success' : 'error'} sx={{ mt: 2 }}>
              {addResult}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Fechar</Button>
          <Button variant="contained" onClick={handleAddBracelets} disabled={addLoading || !addUids.trim()}
            startIcon={addLoading ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ bgcolor: '#6B2FA0', '&:hover': { bgcolor: '#4e2880' } }}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CashlessAdmin;
